import Vue from 'vue';
import self from '@/common/utils/global-config';
import Swagger from '@/common/utils/swagger';
import * as Constants from '@/common/utils/constants';
import * as Messages from '@/common/utils/messages';

/*
计算某一位置距目标位置的距离
position[object]：某一位置
target[object]：目标位置
此外，以上两个参数中都包含以下两个参数
latitude[integration || string]：纬度
longitude[integration || string]：经度
*/
export function calculateDistance(position = {}, target = {}) {
  if (!position.latitude || !position.longitude || !target.latitude || !target.longitude) {
    return -1;
  }
  position.latitude = parseFloat(position.latitude);
  position.longitude = parseFloat(position.longitude);
  target.latitude = parseFloat(target.latitude);
  target.longitude = parseFloat(target.longitude);
  const earthRadius = 6371000;
  const x = Math.cos(position.latitude * Math.PI / 180) * Math.cos(target.latitude * Math.PI / 180) * Math.cos((position.longitude - target.longitude) * Math.PI / 180);
  const y = Math.sin(position.latitude * Math.PI / 180) * Math.sin(target.latitude * Math.PI / 180);
  let s = x + y;
  if (s > 1) s = 1;
  if (s < -1) s = -1;
  const alpha = Math.acos(s);
  return alpha * earthRadius;
}

/*
* 调取字典
* code[array]：字典项名称集合
* */
export function dict(code) {
  return new Promise(resolve => {
    if (self.$sn.validate.empty(code)) {
      resolve([]);
      return false;
    }
    const storage = self.$sn.utils.getHospitalSessionStorage('HospitalDigitalInformation') || {};
    let handle = []; // 已存在字典
    const key = []; // 需调取接口字典项
    code.forEach(el => {
      if (!self.$sn.validate.empty(storage[el])) handle = handle.concat(storage[el]);
      else key.push(el);
    });
    // 如已存在当前全部所需字典，则直接返回
    if (key.length === 0) {
      resolve(handle);
      return false;
    }
    // 如当前缓存中不存在所需字典，则调取接口获取
    self.$sn.utils.ordinaryRequest({
      url: Swagger.getHospitalDigitalInformation,
      query: {
        hosId: self.$sn.utils.getHospitalId(),
        typeKey: key.join(',')
      }
    }).then(result => {
      if (result && result.code === 0 && result.data && result.data.length > 0) {
        handle = handle.concat(self.$sn.format.digitalSort(result.data, '1', 'itemOrder'));
        key.forEach(el => {
          if (result.data.filter(res => res.typeKey === el).length > 0) {
            Vue.set(storage, el, result.data.filter(res => res.typeKey === el));
          }
        });
        self.$sn.utils.setHospitalSessionStorage('HospitalDigitalInformation', storage);
      }
      resolve(handle);
    });
  });
}

export function getHospitalConfigInformation(type = []) {
  return new Promise(resolve => {
    if (self.$sn.validate.empty(type) || !self.$sn.validate.array(type)) {
      resolve(false);
      return false;
    }
    let module = self.$sn.utils.getHospitalSessionStorage('HospitalConfigModuleInformation') || [];
    type = type.filter(result => !module.includes(result));
    if (self.$sn.validate.empty(type)) {
      resolve(true);
      return false;
    }
    self.$sn.utils.ordinaryRequest({
      url: Swagger.getHospitalConfigInformation,
      method: 'POST',
      data: JSON.stringify({
        configType: type.join(','),
        hosId: self.$sn.utils.getHospitalId(),
        platformType: self.$sn.utils.getPlatformType(),
        projectType: self.$sn.utils.getProjectType()
      })
    }).then(result => {
      if (result && result.code === 0 && result.data && result.data.length > 0) {
        module = module.concat(type);
        self.$sn.utils.setHospitalSessionStorage('HospitalConfigModuleInformation', module);
        const format = {};
        result.data.forEach(el => {
          Vue.set(format, el.configKey, el.configValue);
        });
        let config = self.$sn.utils.getHospitalSessionStorage('HospitalConfigInformation') || {};
        config = {
          ...config,
          ...format
        };
        self.$sn.utils.setHospitalSessionStorage('HospitalConfigInformation', config);
        resolve(true);
      }
      else resolve(false);
    });
  });
}

export function getIp() {
  return new Promise(resolve => {
    const params = {
      url: Swagger.getIp,
      method: 'POST'
    };
    self.$sn.utils.ordinaryRequest(params).then(result => {
      let ip = '';
      if (result) ip = result;
      resolve(ip);
    });
  });
}

export function getPrefixUrl() {
  const href = window.location.href;
  const i = href.indexOf('#');
  return i >= 0 ? href.slice(0, i) : href;
}

export function getPrivateResourceUrl(type, url, style, pattern = self.$sn.getConfig('uploadPattern')) {
  return new Promise(resolve => {
    if (self.$sn.validate.empty(url) || !self.$sn.validate.array(url)) resolve([]);
    else {
      const standard = {};
      if (style) Vue.set(standard, 'style', style);
      const gather = [];
      url.forEach(el => {
        gather.push({
          ...standard,
          url: pattern === 'minio' ? el.replace(/(?:net-hospital\/|net-hos-qilu\/|private\/)/g, '') : el
        });
      });
      self.$sn.utils.ordinaryRequest({
        url: Swagger.obtainBizFile,
        query: {
          bizType: type,
          fileUploadType: pattern,
          hosId: self.$sn.utils.getHospitalId()
        },
        method: 'POST',
        header: true,
        data: JSON.stringify({
          urls: gather
        })
      }).then(result => {
        if (result && result.code === 0 && result.data && result.data.length > 0) {
          const res = [];
          result.data.forEach(el => {
            if (el.url) res.push(el.url);
          });
          resolve(res);
        }
        else resolve([]);
      });
    }
  });
}

/*
处理就诊人或住院人信息
patient[object]：就诊人或住院人信息
type[string]：区分类型，对照 constants.js 中的 CLINIC_TYPE
manage[boolean]：未开通就诊卡管理时，是否调用接口格式化卡号
*/
export async function handlePatientInfo(patient = {}, type = Constants.OUT_CARD_CLINIC_TYPE, manage = true) {
  const getCardInfoByCondition = () => {
    return new Promise(resolve => {
      let res = {};
      const params = {
        url: Swagger.getCardInfoByCondition,
        query: {
          patientId: patient.patientId,
          hosId: self.$sn.utils.getHospitalId(),
          hosArea: '',
          bindType: self.$sn.utils.getPlatformType(),
          openId: self.$sn.utils.getOpenId(),
          clinicType: type
        },
        header: true
      };
      self.$sn.utils.ordinaryRequest(params).then(result => {
        if (result && result.code === 0 && result.data) {
          if (result.data.length > 0) res = result.data[0];
        }
        // 前置预留，方便处理后面“获取就诊人建档资料失败，请稍候重试”提示信息
        else res.cardNo = '********';
        resolve(res);
      }).catch(() => {
        resolve(res);
      });
    });
  };
  // 如无就诊人或住院人信息，则直接返回
  if (!patient || !patient.patientId) {
    return {
      code: -1,
      // 使用前请先添加一名就诊人
      msg: Messages.PATIENT001
    };
  }
  // 字段对照
  const field = {
    '1': {
      'title': 'cardTypeName',
      'no': 'outCardNo',
      'id': 'outCardId',
      'encrypt': 'patOutCardNoEncrypt',
      'overdue': 'overdueState'
    },
    '2': {
      'title': 'cardInName',
      'no': 'inCardNo',
      'id': 'inCardId',
      'encrypt': 'patInCardNoEncrypt',
      'overdue': 'inOverdueState'
    }
  };
  // 如当前医院无就诊卡管理，则先进行调取卡列表操作
  if (manage && !self.$sn.getConfig('isOpenCardManage')) {
    if (type === Constants.OUT_CARD_CLINIC_TYPE || (type === Constants.IN_CARD_CLINIC_TYPE && self.$sn.getConfig('inCardInterfaceAndWrite') === '1')) {
      const result = await getCardInfoByCondition();
      if (!self.$sn.validate.empty(result)) {
        patient[field[type].no] = result.cardNo;
        patient[field[type].id] = result.cardId;
        patient[field[type].encrypt] = result.cardNoEncrypt;
      }
    }
  }
  /*
  将住院类型转换为门诊类型，需满足条件如下：
  1）当前为住院类型
  2）住院卡取值为空
  3）当前医院开通一卡通
  */
  if (type === Constants.IN_CARD_CLINIC_TYPE && self.$sn.validate.empty(patient[field[type].no]) && self.$sn.getConfig('isOpenSingleCard')) {
    type = Constants.OUT_CARD_CLINIC_TYPE;
  }
  const final = {
    code: 0,
    data: {
      // 业务逻辑使用卡号
      no: '',
      // 业务展示使用卡号
      caption: ''
    },
    msg: ''
  };
  // 就诊卡或住院卡有值
  if (!self.$sn.validate.empty(patient[field[type].no])) {
    if (patient[field[type].no] === '********') {
      final.code = 1;
      final.data.caption = Messages.PATIENT005;
    }
    else if (patient[field[type].overdue] === Constants.OVER_DUE_STATE) {
      final.code = 4;
      final.data.no = patient[field[type].no];
      final.data.caption = self.$sn.format.caption(Messages.SEARCH018, [self.$sn.getConfig([field[type].title])]);
    }
    else {
      final.data.no = patient[field[type].no];
      if (self.$sn.getConfig('isOpenEncryptedCard')) {
        if (patient[field[type].encrypt] === 'forbid_card_no') {
          final.code = 3;
          final.data.caption = patient[field[type].no] + '将停用，请至窗口办理';
        }
        else {
          final.data.caption = patient[field[type].encrypt];
        }
      }
      else final.data.caption = patient[field[type].no];
    }
  }
  else {
    /*
    社保卡单独处理，需满足条件如下：
    1）当前为门诊类型
    2）当前医院开通社保卡
    3）社保卡取值不为空
    如不满足上述条件，则处理为未绑卡
    */
    if (type === Constants.OUT_CARD_CLINIC_TYPE && self.$sn.getConfig('isOpenSocialSecurityCard') && !self.$sn.validate.empty(patient.socialSecurityCardNo)) {
      final.data.no = patient.socialSecurityCardNo;
      final.data.caption = patient.socialSecurityCardNo;
    }
    else {
      final.code = 2;
      final.data.caption = self.$sn.getConfig('isOpenCardManage') ? '未绑卡' : Messages.PATIENT004;
    }
  }
  return final;
}

/*
修改就诊人信息
id[string]：就诊人 ID
info[object]：要修改的信息
 */
export function modifyPatientInfo(id, info) {
  if (self.$sn.utils.getHospitalLocalStorage('Login_PatientList') && self.$sn.utils.getHospitalLocalStorage('Login_PatientList').length > 0) {
    const storage = self.$sn.utils.getHospitalLocalStorage('Login_PatientList');
    const match = storage.find(result => result.patientId === id);
    if (!self.$sn.validate.empty(match)) {
      const k = Object.keys(info);
      const v = Object.values(info);
      for (let i = 0; i < k.length; i++) {
        Vue.set(match, k[i], v[i]);
      }
      self.$sn.utils.setHospitalLocalStorage('CurrentPatient', match);
      storage.forEach(el => {
        if (el.patientId === id) {
          el = match;
        }
      });
      self.$sn.utils.setHospitalLocalStorage('Login_PatientList', storage);
    }
  }
}

// 删除全部信息，仅保留初始化信息
export function removeEntireInformation() {
  const id = self.$sn.utils.getHospitalId();
  const storage = {
    local: {
      'HospitalInformation': null,
      'HospitalAreaInformation': null
    },
    session: {
      'TimeDisparity': null,
      'HospitalFunctionInformation': null,
      'HospitalDigitalInformation': null
    }
  };
  Object.keys(storage).forEach(type => {
    Object.keys(storage[type]).forEach(title => {
      if (self.$sn.utils['getHospital' + type.substring(0, 1).toUpperCase() + type.substr(1) + 'Storage'](title)) {
        storage[type][title] = self.$sn.utils['getHospital' + type.substring(0, 1).toUpperCase() + type.substr(1) + 'Storage'](title);
      }
    });
  });
  window.localStorage.clear();
  window.sessionStorage.clear();
  self.$sn.utils.setLocalStorage('hosId', id, false);
  Object.keys(storage).forEach(type => {
    Object.keys(storage[type]).forEach(title => {
      if (!self.$sn.validate.empty(storage[type][title])) {
        self.$sn.utils['setHospital' + type.substring(0, 1).toUpperCase() + type.substr(1) + 'Storage'](title, storage[type][title]);
      }
    });
  });
  removeLoginInformation();
}

// 删除登录信息
export function removeLoginInformation() {
  self.$sn.vuex.set('vuex_login_user.' + self.$sn.utils.getHospitalId(), {
    isLogin: false
  });
  removeSpecificStorage({
    local: ['Login_PatientList', 'CurrentPatient']
  });
}

/*
删除多个特定缓存
storage[object]：{ local[array]: ['缓存名称 A', '缓存名称 B'], session[array]: ['缓存名称 C', '缓存名称 D'] }
*/
export function removeSpecificStorage(storage) {
  if (self.$sn.validate.empty(storage) || !self.$sn.validate.object(storage)) return false;
  Object.keys(storage).forEach(type => {
    if (['local', 'session'].includes(type)) {
      const func = 'Hospital' + type.substring(0, 1).toUpperCase() + type.substr(1) + 'Storage';
      storage[type].forEach(title => {
        if (self.$sn.utils['get' + func](title)) {
          self.$sn.utils['remove' + func](title);
        }
      });
    }
  });
}

export function storeRedisInformation(params) {
  return new Promise(resolve => {
    const digital = {
      url: Swagger.storeRedisInformation,
      method: 'POST',
      header: true,
      data: JSON.stringify(params)
    };
    self.$sn.utils.ordinaryRequest(digital).then(() => {
      resolve({});
    });
  });
}

/*
2023年12月8日16:27:37 LXC
type：业务功能类型
file：上传文件
quantity：上传文件几个（int）
size：max 文件大小（eg：3MB → 3 * 1024 * 1024）
method：页面回调方法
 */
export function uploadFile(type, file, quantity = 1, size = Constants.IMAGE_SIZE, method, pattern = self.$sn.getConfig('uploadPattern')) {
  if (self.$sn.validate.empty(type)) {
    method(false, {
      code: 400,
      msg: self.$sn.format.caption(Messages.C018, ['bizType'])
    });
    return false;
  }
  if (file.length > quantity) {
    method(false, {
      code: 400,
      msg: self.$sn.format.caption(Messages.C005, [quantity])
    });
    return false;
  }
  let verify = true;
  for (const el of file) {
    if (el.size && el.size > size) {
      verify = false;
      break;
    }
  }
  if (!verify) {
    method(false, {
      code: 400,
      msg: self.$sn.format.caption(Messages.C006, [size / 1024 / 1024])
    });
    return false;
  }
  const formData = new FormData();
  for (const el of file) {
    formData.append('fileList', el);
  }
  self.$sn.utils.ordinaryRequest({
    url: Swagger.uploadBizFile,
    query: {
      bizType: type,
      fileUploadType: pattern,
      hosId: self.$sn.utils.getHospitalId()
    },
    method: 'POST',
    header: true,
    data: formData
  }).then(result => {
    if (result) {
      if (result.code === 0) {
        if (result.data && result.data.length > 0) {
          const res = [];
          result.data.forEach(el => {
            res.push({
              condition: true,
              trajectory: el,
              suffix: el.split('.')[1]
            });
          });
          method(true, {
            code: pattern,
            msg: self.$sn.format.caption(Messages.C016, ['文件上传'])
          }, res);
        }
        else {
          method(false, {
            code: 400,
            msg: self.$sn.format.caption(Messages.C017, ['文件上传'])
          });
        }
      }
      else {
        method(false, result);
      }
    }
    else {
      method(false, {
        code: 400,
        msg: self.$sn.format.caption(Messages.C017, ['文件上传'])
      });
    }
  });
}
